import React from 'react';
import { observer } from 'mobx-react-lite';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Box, Button, Chip } from '@mui/material';
import { RefreshIcon as Refresh } from "../../../icons";
import { useRootStore } from '../../../store/contexts';
import { graphqlToDateTime } from '../../utils';

interface CacheTableRow {
  entityName: string;
  rows: number;
  sizeKB: number;
  cachedAt: string;
  status: 'loading' | 'loaded' | 'error' | 'not_loaded';
}

export const ReferenceCacheTab: React.FC = observer(() => {
  const rootStore = useRootStore();
  const langStore = rootStore.langStore;
  const authStore = rootStore.authStore;
  const cacheStore = rootStore.referenceCacheStore;

  const stats = cacheStore.getCacheStats();

  // Filter by user permissions (retrieve permission for each entity)
  // Permission format: entityName_retrieve (camelCase + underscore)
  const filteredStats = stats.filter(stat => {
    const entityLower = stat.entityName.charAt(0).toLowerCase() + stat.entityName.slice(1);
    return authStore.hasPermissions(`${entityLower}_retrieve`);
  });

  const cacheData: CacheTableRow[] = filteredStats.map(stat => ({
    entityName: stat.entityName,
    rows: stat.rows,
    sizeKB: stat.sizeKB,
    cachedAt: stat.cachedAt > 0
      ? graphqlToDateTime(new Date(stat.cachedAt))
      : '-',
    status: stat.status,
  }));

  const totalRows = filteredStats.reduce((sum, s) => sum + s.rows, 0);
  const totalKB = filteredStats.reduce((sum, s) => sum + s.sizeKB, 0);

  const handleInvalidateAll = () => {
    cacheStore.invalidateAll();
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { color: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
      loaded: { color: 'success', label: 'Loaded' },
      loading: { color: 'warning', label: 'Loading' },
      error: { color: 'error', label: 'Error' },
      not_loaded: { color: 'default', label: 'Not Loaded' },
    };
    const config = statusConfig[status] || statusConfig.not_loaded;
    return <Chip size="small" color={config.color} label={config.label} />;
  };

  const columns: MRT_ColumnDef<CacheTableRow>[] = [
    {
      accessorKey: 'entityName',
      header: langStore.monitorCacheEntity,
      size: 200,
    },
    {
      accessorKey: 'rows',
      header: langStore.monitorCacheRows,
      size: 100,
    },
    {
      accessorKey: 'sizeKB',
      header: langStore.monitorCacheSize,
      size: 100,
      Cell: ({ cell }) => `${cell.getValue<number>()} KB`,
    },
    {
      accessorKey: 'cachedAt',
      header: langStore.monitorCacheCachedAt,
      size: 180,
    },
    {
      accessorKey: 'status',
      header: langStore.monitorStatus,
      size: 120,
      Cell: ({ cell }) => getStatusChip(cell.getValue<string>()),
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Chip
            label={`${langStore.monitorCacheTotalRows}: ${totalRows}`}
            sx={{ mr: 1 }}
          />
          <Chip
            label={`${langStore.monitorCacheTotalSize}: ${Math.round(totalKB * 100) / 100} KB`}
          />
        </Box>
        <Button
          variant="outlined"
          color="warning"
          startIcon={<Refresh />}
          onClick={handleInvalidateAll}
        >
          {langStore.monitorCacheInvalidateAll}
        </Button>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={cacheData}
        enableColumnResizing
        enableColumnOrdering
        enableDensityToggle
        enableFullScreenToggle={false}
        enablePagination={false}
        initialState={{
          density: 'compact',
        }}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '0.75rem',
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: '0.75rem',
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
});
